import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AnalyzerApiUrl } from '../utilities/constants';
import './AnalyticsDashboard.css';

// ── KPI Strip ──────────────────────────────────────────────────────────────

function KPICard({ label, value, valueColor }) {
  return (
    <Paper variant="outlined" className="kpi-card">
      <Typography variant="caption" className="kpi-label">{label}</Typography>
      <Typography variant="h5" className="kpi-value" sx={{ color: valueColor || '#0f0e2e' }}>
        {value}
      </Typography>
    </Paper>
  );
}

function KPIStrip({ global: g }) {
  const totalComplete = g?.total_jobs_complete ?? 0;
  const totalJobs = g?.total_jobs ?? 0;
  const successRate = totalJobs > 0 ? Math.round((g?.success_rate ?? 0) * 100) : 0;

  const preCompliant = g?.pre_compliance?.compliant ?? 0;
  const postCompliant = g?.post_compliance?.compliant ?? 0;
  const preNonCompliant = g?.pre_compliance?.non_compliant ?? 0;
  const complianceUplift = preNonCompliant > 0
    ? Math.round((Math.max(0, postCompliant - preCompliant) / preNonCompliant) * 100)
    : null;

  const figs = g?.total_figures_tagged ?? 0;
  const figsNoAlt = g?.total_figures_without_alt ?? 0;
  const altTextCoverage = (figs + figsNoAlt) > 0
    ? Math.round((figs / (figs + figsNoAlt)) * 100)
    : null;

  return (
    <div className="kpi-strip">
      <KPICard label="Total Jobs" value={totalJobs.toLocaleString()} />
      <KPICard label="Pages Remediated" value={(g?.total_pages_remediated ?? 0).toLocaleString()} />
      <KPICard label="Elements Tagged" value={(g?.total_elements_tagged ?? 0).toLocaleString()} />
      <KPICard
        label="Compliance Uplift"
        value={complianceUplift !== null ? `${complianceUplift > 0 ? '+' : ''}${complianceUplift}%` : '—'}
        valueColor={
          complianceUplift === null ? '#0f0e2e'
          : complianceUplift >= 50 ? '#16a34a'
          : complianceUplift >= 20 ? '#d97706'
          : '#dc2626'
        }
      />
      <KPICard
        label="Alt Text Coverage"
        value={altTextCoverage !== null ? `${altTextCoverage}%` : '—'}
        valueColor={
          altTextCoverage === null ? '#0f0e2e'
          : altTextCoverage >= 90 ? '#16a34a'
          : altTextCoverage >= 70 ? '#d97706'
          : '#dc2626'
        }
      />
      <KPICard
        label="Success Rate"
        value={totalJobs > 0 ? `${successRate}%` : '—'}
        valueColor={successRate >= 90 ? '#16a34a' : successRate >= 70 ? '#d97706' : successRate > 0 ? '#dc2626' : '#0f0e2e'}
      />
    </div>
  );
}

// ── Page Pool Gauge ────────────────────────────────────────────────────────

function PagePoolGauge({ pagePool }) {
  const consumed = pagePool?.consumed ?? 0;
  const limit = pagePool?.limit ?? 1;
  const pct = Math.min(100, Math.round((consumed / limit) * 100));
  const color = pct >= 90 ? '#dc2626' : pct >= 75 ? '#d97706' : '#16a34a';
  const deg = (pct / 100) * 360;

  return (
    <Paper variant="outlined" className="widget-card" component="section" aria-labelledby="page-pool-title">
      <Typography variant="subtitle2" className="widget-title" id="page-pool-title">Shared Page Pool</Typography>
      <div className="gauge-container">
        <div
          className="gauge-ring"
          style={{
            background: `conic-gradient(${color} 0deg ${deg}deg, #e2e8f0 ${deg}deg 360deg)`,
          }}
          role="img"
          aria-label={`Page pool ${pct}% used: ${consumed.toLocaleString()} of ${limit.toLocaleString()} pages consumed.`}
        >
          <div className="gauge-inner" aria-hidden="true">
            <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{pct}%</Typography>
            <Typography variant="caption" sx={{ color: '#475569' }}>used</Typography>
          </div>
        </div>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {consumed.toLocaleString()} / {limit.toLocaleString()} pages
        </Typography>
      </div>
    </Paper>
  );
}

// ── Compliance Impact ──────────────────────────────────────────────────────

function ComplianceImpact({ global: g }) {
  const preCompliant = g?.pre_compliance?.compliant ?? 0;
  const preNonCompliant = g?.pre_compliance?.non_compliant ?? 0;
  const preTotal = preCompliant + preNonCompliant;
  const postCompliant = g?.post_compliance?.compliant ?? 0;
  const postNonCompliant = g?.post_compliance?.non_compliant ?? 0;
  const postTotal = postCompliant + postNonCompliant;
  const improved = Math.max(0, postCompliant - preCompliant);

  const preNonPct = preTotal > 0 ? Math.round((preNonCompliant / preTotal) * 100) : 0;
  const postPct = postTotal > 0 ? Math.round((postCompliant / postTotal) * 100) : 0;

  return (
    <Paper variant="outlined" className="widget-card" component="section" aria-labelledby="compliance-impact-title">
      <Typography variant="subtitle2" className="widget-title" id="compliance-impact-title">Compliance Impact</Typography>
      <div
        className="compliance-row"
        role="group"
        aria-label={`Before remediation: ${preNonCompliant.toLocaleString()} of ${preTotal.toLocaleString()} documents non-compliant (${preNonPct} percent). After remediation: ${postCompliant.toLocaleString()} of ${postTotal.toLocaleString()} documents compliant (${postPct} percent).`}
      >
        <div className="compliance-col">
          <Typography variant="caption" className="compliance-label">Before</Typography>
          <div className="compliance-badge noncompliant">
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#b91c1c', lineHeight: 1 }}>
              {preTotal > 0 ? `${preNonPct}%` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#b91c1c', fontWeight: 700, display: 'block' }}>
              NON-COMPLIANT
            </Typography>
          </div>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
            {preNonCompliant.toLocaleString()} docs
          </Typography>
        </div>

        <Typography sx={{ color: '#475569', fontWeight: 700, fontSize: '1.4rem', mt: 2 }} aria-hidden="true">→</Typography>

        <div className="compliance-col">
          <Typography variant="caption" className="compliance-label">After</Typography>
          <div className="compliance-badge compliant">
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#15803d', lineHeight: 1 }}>
              {postTotal > 0 ? `${postPct}%` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700, display: 'block' }}>
              COMPLIANT
            </Typography>
          </div>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
            {postCompliant.toLocaleString()} docs
          </Typography>
        </div>
      </div>

      {improved > 0 && (
        <div className="compliance-summary">
          <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 700 }}>
            +{improved.toLocaleString()} documents now compliant
          </Typography>
        </div>
      )}
    </Paper>
  );
}

// ── User Usage Table ───────────────────────────────────────────────────────

function UserUsageTable({ users }) {
  const [sortKey, setSortKey] = useState('jobs');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...(users || [])].sort((a, b) => {
    const va = sortKey === 'last_activity' ? (a.last_activity || '') : (a[sortKey] ?? 0);
    const vb = sortKey === 'last_activity' ? (b.last_activity || '') : (b[sortKey] ?? 0);
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sortDir === 'desc' ? -cmp : cmp;
  }).slice(0, 50);

  const cols = [
    { key: 'jobs', label: 'Jobs', align: 'right' },
    { key: 'pages', label: 'Pages', align: 'right' },
    { key: 'success_rate', label: 'Success %', align: 'right' },
    { key: 'last_activity', label: 'Last Activity', align: 'left' },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: '10px', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography variant="subtitle2" className="widget-title" sx={{ mb: 0 }}>
          Usage by User
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small" aria-label="Usage by user">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, color: '#0f0e2e' }} scope="col">User</TableCell>
              {cols.map(({ key, label, align }) => (
                <TableCell key={key} align={align} sx={{ fontWeight: 700, color: '#0f0e2e' }} scope="col">
                  <TableSortLabel
                    active={sortKey === key}
                    direction={sortKey === key ? sortDir : 'desc'}
                    onClick={() => handleSort(key)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((u) => {
              const successPct = u.jobs > 0 ? Math.round((u.success_rate ?? 0) * 100) : null;
              const successColor = successPct === null ? '#0f0e2e'
                : successPct >= 90 ? '#16a34a'
                : successPct >= 70 ? '#d97706'
                : '#dc2626';
              return (
                <TableRow key={u.email} hover>
                  <TableCell
                    sx={{
                      color: '#0f0e2e',
                      maxWidth: 220,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={u.email}
                  >
                    {u.email}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#0f0e2e' }}>
                    {(u.jobs ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#0f0e2e' }}>
                    {(u.pages ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: successColor, fontWeight: 600 }}>
                    {successPct !== null ? `${successPct}%` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#0f0e2e' }}>
                    {u.last_activity ? new Date(u.last_activity).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: '#64748b', py: 3 }}>
                  No user data yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────

function AnalyticsSkeleton() {
  return (
    <div className="analytics-root">
      <Skeleton variant="text" width={160} height={40} sx={{ mb: 1 }} />
      <div className="kpi-strip">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: '10px' }} />
        ))}
      </div>
      <div className="analytics-row">
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '10px' }} />
      </div>
      <Skeleton variant="rounded" height={260} sx={{ borderRadius: '10px' }} />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function AnalyticsDashboard({ idToken }) {
  const [state, setState] = useState({ data: null, loading: false, error: null });
  const retryRef = useRef(null);

  const fetchAnalytics = useCallback(async () => {
    if (!idToken) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const resp = await fetch(`${AnalyzerApiUrl}/analytics`, {
        headers: { Authorization: idToken },
      });
      if (!resp.ok) throw new Error(`Analytics API returned ${resp.status}`);
      const data = await resp.json();
      setState({ data, loading: false, error: null });
      if (data.backfill_in_progress) {
        retryRef.current = setTimeout(fetchAnalytics, 5000);
      }
    } catch (err) {
      if (retryRef.current) clearTimeout(retryRef.current);
      setState({ data: null, loading: false, error: err.message });
    }
  }, [idToken]);

  useEffect(() => {
    fetchAnalytics();
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, [fetchAnalytics]);

  const { data, loading, error } = state;

  if (loading && !data) return <AnalyticsSkeleton />;

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert
          severity="error"
          action={<Button size="small" onClick={fetchAnalytics}>Retry</Button>}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <div className="analytics-root">
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{ fontWeight: 700, color: '#0f0e2e', flexGrow: 1 }}
        >
          Analytics
        </Typography>
        <Tooltip title="Refresh analytics">
          <IconButton
            onClick={fetchAnalytics}
            aria-label="Refresh analytics"
            size="small"
            disabled={loading}
          >
            {loading
              ? <CircularProgress size={20} aria-label="Refreshing" />
              : <RefreshIcon aria-hidden="true" />}
          </IconButton>
        </Tooltip>
      </Box>

      {data?.backfill_in_progress && (
        <Alert severity="info">
          Analytics data is being compiled from historical jobs. Refreshing in 5s…
        </Alert>
      )}

      <KPIStrip global={data?.global} />

      <div className="analytics-row">
        <PagePoolGauge pagePool={data?.page_pool} />
        <ComplianceImpact global={data?.global} />
      </div>

      <UserUsageTable users={data?.users} />
    </div>
  );
}
